import { useMemo } from 'react';
import { getGatewayBaseUrl } from '../api/client.ts';
import { DOCS_ACCESS_BRAND_NAME } from '../api/docs.ts';
import { useCatalog } from '../api/useCatalog.ts';
import { useDocsAccess } from '../api/useDocsAccess.ts';
import { useT } from '../i18n/index.ts';
import { isSupportedDocsPresentationVersion } from './docs/document.ts';
import { HandwrittenDocs } from './docs/HandwrittenDocs.tsx';
import { docsAvailableCapabilities, resolveDocsModelValues } from './docs/models.ts';
import type { DocsPlaceholderValues } from './docs/placeholders.ts';
import { ServedDocs } from './docs/ServedDocs.tsx';
import { KEY_PLACEHOLDER } from './docsSnippets.ts';

/**
 * `/docs` — integration docs. Prefers the session-gated
 * `GET /v1/docs/access` document when this build understands its
 * `presentation_version`; otherwise (unknown version, fetch error, or
 * still loading) the handwritten page stays on screen so the route is
 * never blank. Deleting the handwritten copy is #152. The three-card
 * endpoint strip (`EndpointCards`) is environment-derived local chrome
 * and stays on both views — it is not part of the served document.
 */
export function Docs(): JSX.Element {
  const t = useT();
  const docsQ = useDocsAccess();
  const catalogQ = useCatalog();

  const served = docsQ.data;
  const canRenderServed =
    docsQ.status === 'success' &&
    served !== undefined &&
    isSupportedDocsPresentationVersion(served.presentation_version);

  const placeholders = useMemo((): DocsPlaceholderValues | null => {
    if (!canRenderServed || served === undefined) return null;
    const gatewayOrigin = getGatewayBaseUrl();
    const consoleOrigin = typeof window === 'undefined' ? '' : window.location.origin;
    return {
      static: {
        gatewayOrigin,
        openaiBase: `${gatewayOrigin}/v1`,
        consoleOrigin,
        apiKeyPlaceholder: KEY_PLACEHOLDER,
        brandName: DOCS_ACCESS_BRAND_NAME,
      },
      models: resolveDocsModelValues(served.model_selectors, catalogQ.data),
    };
  }, [canRenderServed, served, catalogQ.data]);

  const availableCapabilities = useMemo(
    () =>
      served === undefined
        ? null
        : docsAvailableCapabilities(served.model_selectors, catalogQ.data),
    [served, catalogQ.data],
  );

  if (canRenderServed && served !== undefined && placeholders !== null) {
    return (
      <ServedDocs
        document={served}
        placeholders={placeholders}
        availableCapabilities={availableCapabilities}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {docsQ.isPending ? (
        <p className="m-0 text-label text-ink-low" data-testid="docs-served-loading">
          {t('docs.served.loading')}
        </p>
      ) : null}
      {docsQ.isError ? (
        <p className="m-0 text-label text-ink-low" data-testid="docs-served-error">
          {t('docs.served.error')}
        </p>
      ) : null}
      <HandwrittenDocs />
    </div>
  );
}
